import moment from 'moment';

/**
 * Panel metrikleri — hepsi rezervasyon listesinden ve kontenjan verisinden üretilir.
 *
 * Kaynak: property/booking/list  (filter: date_type=reservation_time, date_range)
 * Liste satiri:
 *   { id, channel_id, booking_channel: {name}, booking_code, booking_contact: {name, surname},
 *     checkin_date, checkout_date, total, currency_code, reservation_time (unix), status }
 *
 * status kodlari (backend: booking_status tablosu):
 *   0 iptal/iade · 1 onayli · 2 on rezervasyon · 3 no-show (gelmedi)
 *
 * room_count / room_nights alanlari 09.09.2026'da eklendi. room_nights, odalarin
 * kendi tarih araliklarindan hesaplaniyor; room_count x (checkout - checkin)
 * carpimi yanlis sonuc verir (bir rezervasyonun odalari farkli tarihlerde olabilir).
 * room_count = 0 gelen kayitta oda satiri yok — oda gecesi hesabina katilmaz.
 */

export const BOOKING_STATUS = {
    CANCELLED: 0,
    CONFIRMED: 1,
    PENDING: 2,
    NO_SHOW: 3,
};

const asArray = (list) => (Array.isArray(list) ? list : []);

// Iptal disindaki her kayit "gelen rezervasyon" sayilir — on rezervasyon dahil,
// cunku talebi gosteriyor.
const isActive = (booking) => Number(booking.status) !== BOOKING_STATUS.CANCELLED;
const isCancelled = (booking) => Number(booking.status) === BOOKING_STATUS.CANCELLED;
const isNoShow = (booking) => Number(booking.status) === BOOKING_STATUS.NO_SHOW;
const isPending = (booking) => Number(booking.status) === BOOKING_STATUS.PENDING;

/**
 * Ciroya giren kayit: iptal ve on rezervasyon haric.
 *
 * On rezervasyonun odemesi tamamlanmamis (booking_payment.total = null) ve ayni
 * konaklama odeme basarili olunca ikinci bir kayit olarak geliyor. Ikisini de
 * saymak ciroyu katliyordu — ornek: bir konaklama icin 3 kayit, 3 x 147 EUR.
 * Adet sayimlarinda duruyorlar, ciroda durmuyorlar.
 *
 * No-show ciroda: oda satildi, para alindi, misafir gelmedi.
 */
const isEarned = (booking) => isActive(booking) && !isPending(booking);

const nightsOf = (booking) => {
    const from = moment(booking.checkin_date, 'YYYY-MM-DD');
    const to = moment(booking.checkout_date, 'YYYY-MM-DD');
    if (!from.isValid() || !to.isValid()) return 0;
    return Math.max(0, to.diff(from, 'days'));
};

// Satilan oda gecesi. Backend hesapliyor; alan yoksa (eski yanit) 0 doner ve
// hesaba katilmaz — "her rezervasyon 1 odadir" varsayimi yapilmiyor.
const roomNightsOf = (booking) => {
    const value = Number(booking.room_nights);
    return Number.isFinite(value) && value > 0 ? value : 0;
};

const bookedOn = (booking) => moment.unix(Number(booking.reservation_time));

/** Para birimi basina toplam — kurlar toplanmaz, ayri ayri tutulur. */
export function sumByCurrency(bookings) {
    const totals = {};
    asArray(bookings).forEach((booking) => {
        const code = booking.currency_code || '—';
        totals[code] = (totals[code] || 0) + Number(booking.total || 0);
    });
    return totals;
}

/** En yuksek tutarli para birimi ana rakam olarak gosterilir, digerleri yaninda. */
export function primaryCurrency(totals) {
    const entries = Object.entries(totals || {});
    if (!entries.length) return {code: null, amount: 0, others: []};
    entries.sort((a, b) => b[1] - a[1]);
    return {
        code: entries[0][0],
        amount: entries[0][1],
        others: entries.slice(1).map(([code, amount]) => ({code, amount})),
    };
}

/** Ciroya giren kayitlari suzer — on rezervasyon ve iptal disarida */
export function earnedOnly(bookings) {
    return asArray(bookings).filter(isEarned);
}

/** On rezervasyonlar — ciroya girmeyen ama sayilan kayitlar */
export function pendingOnly(bookings) {
    return asArray(bookings).filter((b) => isActive(b) && isPending(b));
}

/** Bugun rezervasyonu yapilanlar (reservation_time bugun) */
export function todaysBookings(bookings) {
    const today = moment().startOf('day');
    return asArray(bookings).filter((booking) => bookedOn(booking).isSame(today, 'day'));
}

/** Bugun otele giris / cikis yapacaklar — iptaller sayilmaz */
export function todaysMovements(bookings) {
    const today = moment().format('YYYY-MM-DD');
    const active = asArray(bookings).filter(isActive);
    return {
        checkIn: active.filter((b) => b.checkin_date === today).length,
        checkOut: active.filter((b) => b.checkout_date === today).length,
    };
}

/** Kanal bazinda adet, ciro ve gecelik ortalama fiyat */
/**
 * Meta / referans kanallari. Rezervasyon Kaynaklari tablosunda gosterilmezler;
 * ciro ve adet toplamlarina dahil olmaya devam ederler.
 *
 * GECICI COZUM: ada gore eslesme. Dogrusu channel_category_id ile ayirmak ama
 * hangi kategorinin meta oldugu backend'de tanimli degil — Bulut kategori
 * id'sini verdiginde bu liste silinip tek bir id kontrolune dusecek.
 * O zamana kadar yeni bir meta eklenirse buraya da eklenmeli.
 */
const META_CHANNEL_PATTERNS = [
    'google hotel',
    'trivago',
    'yandex',
    'mirai',
    'tripadvisor',
    'trip advisor',
    'sojern',
    'chatgpt',
    'instagram',
    'ig'
];

export function isMetaChannel(name) {
    const normalized = String(name || '').toLocaleLowerCase('tr');
    return META_CHANNEL_PATTERNS.some((pattern) => normalized.includes(pattern));
}

export function channelBreakdown(bookings) {
    const active = asArray(bookings).filter(isActive);
    const map = new Map();

    active.forEach((booking) => {
        const name = (booking.booking_channel && booking.booking_channel.name)
            || `Kanal ${booking.channel_id}`;
        const entry = map.get(name) || {
            name, count: 0, earnedCount: 0, revenue: {}, nights: 0, roomNights: 0,
        };

        entry.count += 1;
        entry.nights += nightsOf(booking);

        // Adet tum aktif kayitlardan, ciro yalnizca parasi alinanlardan
        if (isEarned(booking)) {
            entry.earnedCount += 1;
            entry.roomNights += roomNightsOf(booking);
            const code = booking.currency_code || '—';
            entry.revenue[code] = (entry.revenue[code] || 0) + Number(booking.total || 0);
        }

        map.set(name, entry);
    });

    const rows = Array.from(map.values()).map((entry) => {
        const primary = primaryCurrency(entry.revenue);
        return {
            ...entry,
            primaryRevenue: primary,
            // ABV = ciro / rezervasyon adedi. Payda earnedCount: ciro on
            // rezervasyonu icermiyor, adet icerseydi ortalama yapay dusuk cikardi.
            abv: entry.earnedCount > 0 ? primary.amount / entry.earnedCount : null,
            // ADR = ciro / satilan oda gecesi; oda verisi yoksa hesaplanmaz
            adr: entry.roomNights > 0 ? primary.amount / entry.roomNights : null,
        };
    });

    rows.sort((a, b) => b.count - a.count);

    const total = rows.reduce((sum, row) => sum + row.count, 0);
    const max = rows.length ? rows[0].count : 0;

    // Yuzde ve bar, gizlenenler dahil TUM kanallara gore hesaplanir: gorunen bir
    // kanalin payi, donemin gercek payi olsun. Gorunen satirlarin toplami %100'e
    // ulasmaz — zaten bir kismi gizli, tabloda da yaziyor.
    return rows.map((row) => ({
        ...row,
        isMeta: isMetaChannel(row.name),
        share: total > 0 ? (row.count / total) * 100 : 0,
        // Bar genisligi en cok adede gore olceklenir
        barWidth: max > 0 ? (row.count / max) * 100 : 0,
    }));
}

/** Donem ozeti: adet, ciro, ADR, ortalama konaklama, rezervasyon penceresi, iptal */
export function periodSummary(bookings) {
    const all = asArray(bookings);
    const active = all.filter(isActive);
    const cancelled = all.filter(isCancelled);

    const earned = all.filter(isEarned);
    const pending = active.filter(isPending);

    const nights = active.reduce((sum, b) => sum + nightsOf(b), 0);
    // Ciro ve ADR yalnizca parasi alinan kayitlardan; "satilan oda gecesi" de oyle
    const roomNights = earned.reduce((sum, b) => sum + roomNightsOf(b), 0);
    const revenue = primaryCurrency(sumByCurrency(earned));
    const noShow = earned.filter(isNoShow);

    // Rezervasyon penceresi: girise kalan gun (rezervasyon anindan)
    const windows = active
        .map((b) => moment(b.checkin_date, 'YYYY-MM-DD').diff(bookedOn(b).startOf('day'), 'days'))
        .filter((days) => Number.isFinite(days) && days >= 0);

    return {
        count: active.length,
        revenue,
        nights,
        roomNights,
        // ADR = ciro / satilan oda gecesi. Konaklama gecesine bolmek, cok odali
        // rezervasyonlarda fiyati kat kat yuksek gosteriyordu.
        adr: roomNights > 0 ? revenue.amount / roomNights : null,
        avgStay: active.length > 0 ? nights / active.length : null,
        noShowCount: noShow.length,
        noShowRevenue: primaryCurrency(sumByCurrency(noShow)),
        // Ciroya girmeyen on rezervasyonlar — sayilar birbirini tutsun diye gosteriliyor
        pendingCount: pending.length,
        pendingRevenue: primaryCurrency(sumByCurrency(pending)),
        bookingWindow: windows.length
            ? Math.round(windows.reduce((a, b) => a + b, 0) / windows.length)
            : null,
        cancelledCount: cancelled.length,
        cancelledRevenue: primaryCurrency(sumByCurrency(cancelled)),
        cancelRate: all.length > 0 ? (cancelled.length / all.length) * 100 : 0,
    };
}

/** Gunluk rezervasyon adedi — trend grafigi icin. Pencere endDate'te biter (varsayilan bugun). */
export function dailyTrend(bookings, days = 30, endDate = null) {
    const end = endDate ? moment(endDate).startOf('day') : moment().startOf('day');
    const start = end.clone().subtract(days - 1, 'days');
    const buckets = [];

    for (let i = 0; i < days; i += 1) {
        buckets.push({date: start.clone().add(i, 'days'), count: 0, rows: []});
    }

    asArray(bookings).filter(isActive).forEach((booking) => {
        const when = bookedOn(booking).startOf('day');
        const index = when.diff(start, 'days');
        if (index >= 0 && index < days) {
            buckets[index].count += 1;
            buckets[index].rows.push(booking);
        }
    });

    // Gunun cirosu ipucu kartinda gosteriliyor; satirlari tasimaya gerek yok
    buckets.forEach((bucket) => {
        bucket.revenue = primaryCurrency(sumByCurrency(bucket.rows.filter(isEarned)));
        delete bucket.rows;
    });

    const counts = buckets.map((b) => b.count);
    const total = counts.reduce((a, b) => a + b, 0);

    return {
        points: buckets,
        total,
        average: days > 0 ? total / days : 0,
        max: counts.length ? Math.max(...counts) : 0,
    };
}

/** Verilen tarih araligina (gun bazinda, iki uc dahil) dusen rezervasyonlar */
export function bookingsInRange(bookings, start, end) {
    const from = moment(start).startOf('day');
    const to = moment(end).endOf('day');
    return asArray(bookings).filter((booking) => bookedOn(booking).isBetween(from, to, null, '[]'));
}

/** Son N rezervasyon — liste zaten id DESC geliyor */
export function latestBookings(bookings, limit = 10) {
    return asArray(bookings).slice(0, limit);
}

/**
 * Kontenjan verisinden oda ve musaitlik ozeti.
 * inventory: [{id, name, room_availability: {tarih: {value, stop_sell}}}]
 */
export function availabilitySummary(inventory) {
    const rooms = asArray(inventory);
    if (!rooms.length) return null;

    const openOn = (entry) => {
        if (!entry) return false;
        if (entry.stop_sell === true || Number(entry.stop_sell) === 1) return false;
        return Number(entry.value) > 0;
    };

    // Bugun satisa acik oda tipi sayisi
    const today = moment().format('YYYY-MM-DD');
    const activeRooms = rooms.filter((room) => openOn((room.room_availability || {})[today])).length;

    // Gun bazinda: kac oda tipi acik
    const dates = Array.from(
        new Set(rooms.flatMap((room) => Object.keys(room.room_availability || {})))
    ).sort();

    const byDate = dates.map((date) => {
        const openRooms = rooms.filter((room) => openOn((room.room_availability || {})[date]));
        // Kapali oda adlari ve kalan kontenjan takvim ipucunda gosteriliyor
        const closed = rooms
            .filter((room) => !openOn((room.room_availability || {})[date]))
            .map((room) => room.name)
            .filter(Boolean);
        const units = openRooms.reduce(
            (sum, room) => sum + Number(((room.room_availability || {})[date] || {}).value || 0),
            0
        );
        return {date, open: openRooms.length, total: rooms.length, units, closed};
    });

    // Gun sayimlari. Onceden "bugunden itibaren kesintisiz seri" tutuluyordu; yarin
    // tek bir oda tipi kapaliysa seri sifirlaniyor ve takvimin tamami yesilken
    // "0 gun" yaziyordu. Seri yerine gun adedi hem dogru hem eyleme donuk.
    const ahead = byDate.filter((day) => !moment(day.date, 'YYYY-MM-DD').isBefore(moment(), 'day'));
    const openDays = ahead.filter((day) => day.open === day.total).length;
    const partialDays = ahead.filter((day) => day.open > 0 && day.open < day.total).length;
    const closedDays = ahead.filter((day) => day.open === 0).length;

    // Takvimde ilk sorunlu gun: "ne zamana kadar sorunsuz" sorusunun duz karsiligi
    const firstGap = ahead.find((day) => day.open < day.total);

    return {
        totalRooms: rooms.length,
        activeRooms,
        closedRooms: rooms.length - activeRooms,
        totalDays: ahead.length,
        openDays,
        partialDays,
        closedDays,
        firstGapDate: firstGap ? firstGap.date : null,
        byDate,
    };
}

/** Sayi bicimleme — panelde her yerde ayni gorunsun */
export const formatNumber = (value) => new Intl.NumberFormat('tr-TR').format(Math.round(Number(value) || 0));
export const formatMoney = (value) => new Intl.NumberFormat('tr-TR', {maximumFractionDigits: 0}).format(Number(value) || 0);
